import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  BookOpen, ArrowLeft, Trophy, Crown, Medal, Award, Star
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaderboardPage = ({ user, userType }) => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, [classFilter]);

  const fetchLeaderboard = async () => {
    try {
      const url = classFilter 
        ? `${API}/leaderboard?class_name=${encodeURIComponent(classFilter)}`
        : `${API}/leaderboard`;
      const response = await axios.get(url);
      setLeaderboard(response.data);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (index) => {
    switch (index) {
      case 0:
        return <Crown className="w-6 h-6 text-[#D4AF37]" />;
      case 1:
        return <Medal className="w-6 h-6 text-[#C0C0C0]" />;
      case 2:
        return <Medal className="w-6 h-6 text-[#CD7F32]" />;
      default:
        return <span className="w-6 h-6 flex items-center justify-center font-bold text-[#64748B]">{index + 1}</span>;
    }
  };

  const getRankClass = (index) => {
    switch (index) {
      case 0:
        return "bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/5 border-[#D4AF37]";
      case 1:
        return "bg-gradient-to-r from-[#C0C0C0]/20 to-[#C0C0C0]/5 border-[#C0C0C0]";
      case 2:
        return "bg-gradient-to-r from-[#CD7F32]/20 to-[#CD7F32]/5 border-[#CD7F32]";
      default:
        return "bg-[#F8F9FA] border-[#E2E8F0]";
    }
  };

  const isCurrentUser = (student) => {
    return student.id === user.id;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="leaderboard-page">
      {/* Navigation */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(userType === "director" ? "/director" : "/dashboard")}
            className="text-[#1A365D]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#D4AF37] flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#0F172A]" />
          </div>
          <span className="navbar-logo hidden sm:block">Classement</span>
        </div>
      </nav>

      <main className="px-4 py-6 max-w-3xl mx-auto page-enter">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Classement des Philosophes
          </h1>
          <p className="text-[#64748B]">
            Les meilleurs élèves de Terminale en philosophie
          </p>
        </div>

        {/* Class Filter */}
        <Tabs value={classFilter} onValueChange={setClassFilter} className="mb-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="" data-testid="filter-all">Tous</TabsTrigger>
            <TabsTrigger value="Terminale A" data-testid="filter-a">Term. A</TabsTrigger>
            <TabsTrigger value="Terminale B" data-testid="filter-b">Term. B</TabsTrigger>
            <TabsTrigger value="Terminale C" data-testid="filter-c">Term. C</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Top 3 Podium */}
        {leaderboard.length >= 3 && (
          <div className="flex justify-center items-end gap-4 mb-8">
            {/* Second Place */}
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 rounded-full bg-[#C0C0C0]/20 flex items-center justify-center border-4 border-[#C0C0C0]">
                <span className="text-xl md:text-2xl font-bold text-[#1A365D]">
                  {leaderboard[1]?.first_name?.[0]}{leaderboard[1]?.last_name?.[0]}
                </span>
              </div>
              <Medal className="w-6 h-6 text-[#C0C0C0] mx-auto mb-1" />
              <p className="text-sm font-medium text-[#1A365D] truncate max-w-20">
                {leaderboard[1]?.first_name}
              </p>
              <p className="text-xs text-[#64748B]">{leaderboard[1]?.points} pts</p>
            </div>

            {/* First Place */}
            <div className="text-center -mt-4">
              <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-2 rounded-full bg-[#D4AF37]/20 flex items-center justify-center border-4 border-[#D4AF37]">
                <span className="text-2xl md:text-3xl font-bold text-[#1A365D]">
                  {leaderboard[0]?.first_name?.[0]}{leaderboard[0]?.last_name?.[0]}
                </span>
              </div>
              <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-1" />
              <p className="text-sm font-medium text-[#1A365D] truncate max-w-24">
                {leaderboard[0]?.first_name}
              </p>
              <p className="text-xs text-[#64748B]">{leaderboard[0]?.points} pts</p>
            </div>

            {/* Third Place */}
            <div className="text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-2 rounded-full bg-[#CD7F32]/20 flex items-center justify-center border-4 border-[#CD7F32]">
                <span className="text-xl md:text-2xl font-bold text-[#1A365D]">
                  {leaderboard[2]?.first_name?.[0]}{leaderboard[2]?.last_name?.[0]}
                </span>
              </div>
              <Medal className="w-6 h-6 text-[#CD7F32] mx-auto mb-1" />
              <p className="text-sm font-medium text-[#1A365D] truncate max-w-20">
                {leaderboard[2]?.first_name}
              </p>
              <p className="text-xs text-[#64748B]">{leaderboard[2]?.points} pts</p>
            </div>
          </div>
        )}

        {/* Full Leaderboard */}
        <Card className="card-sjh">
          <CardHeader>
            <CardTitle className="text-lg text-[#1A365D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Classement complet
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-[#64748B]">Chargement...</div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-8 text-[#64748B]">
                Aucun élève inscrit pour le moment
              </div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map((student, index) => (
                  <div 
                    key={index}
                    className={`flex items-center gap-4 p-3 rounded-lg border ${getRankClass(index)} ${
                      isCurrentUser(student) ? 'ring-2 ring-[#1A365D]' : ''
                    }`}
                    data-testid={`leaderboard-item-${index}`}
                  >
                    <div className="flex-shrink-0">
                      {getRankIcon(index)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#1A365D] truncate">
                        {student.first_name} {student.last_name}
                        {isCurrentUser(student) && (
                          <Badge className="ml-2 bg-[#1A365D]">Vous</Badge>
                        )}
                      </p>
                      <p className="text-xs text-[#64748B]">{student.class_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="border-[#1A365D] text-[#1A365D]">
                        Niv. {student.level}
                      </Badge>
                      <div className="flex items-center gap-1 bg-[#D4AF37]/10 px-2 py-1 rounded">
                        <Star className="w-4 h-4 text-[#D4AF37]" />
                        <span className="font-semibold text-[#1A365D]">{student.points}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LeaderboardPage;
